/**
 * The i18next instance.
 *
 * This module owns *strings*. It does not own the locale — `AppearanceProvider`
 * does, because the same choice drives `lang` and `dir` on `<html>` and those
 * must have exactly one writer. `I18nProvider` is the subscription: it watches
 * `useAppearance().locale` and calls `changeLanguage`.
 *
 * The plan's version hard-codes `lng: 'en'` and never changes it, which means an
 * agent who picks Arabic gets a mirrored layout full of English. Two things fix
 * that, and both are needed:
 *
 *  1. `I18nProvider`'s effect, for the switch at runtime.
 *  2. The initial `lng` below, for the *reload*. `readStoredAppearance()` is the
 *     same enum-validated read the provider and the `index.html` boot snippet
 *     use, so an Arabic session comes back Arabic on the very first render and
 *     `{"locale":"tlh"}` can never reach i18next. Booting at `'en'` and letting
 *     the effect correct it would show a frame of English — the string
 *     equivalent of the flash task 18 removed.
 *
 * There is deliberately no `lang` / `dir` write anywhere in this file. i18next
 * offers `i18n.dir()`; using it here would create a second owner of the global
 * switch that could disagree with the provider, which is precisely the bug
 * task 18's D2 mutation models. `i18n.test.ts` enforces the absence.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { LOCALES, readStoredAppearance } from '../design-system/providers/appearance';
import ar from './ar.json';
import en from './en.json';

// The Arabic type remap travels with the layer that turns Arabic on, so any
// entry point that mounts i18n gets it. It is keyed on `:root[lang="ar"]`, whose
// specificity beats tokens.css's `:root`, so import order does not matter.
import './arabic.css';

/**
 * One namespace. The application is a single bundle with no lazy routes, so
 * namespace splitting would buy nothing and cost every call site a prefix.
 */
export const DEFAULT_NS = 'translation';

export const resources = {
  en: { [DEFAULT_NS]: en },
  ar: { [DEFAULT_NS]: ar },
} as const;

void i18next.use(initReactI18next).init({
  resources,
  lng: readStoredAppearance().locale,
  fallbackLng: 'en',
  supportedLngs: [...LOCALES],
  defaultNS: DEFAULT_NS,
  interpolation: {
    // React escapes everything it renders; escaping again turns an apostrophe
    // into `&#39;` on screen.
    escapeValue: false,
  },
});

export default i18next;
